<template>
  <div class="sms-thread">
    <div v-if="!contact.phone" class="text-muted text-center py-4">
      This contact has no phone number on file.
    </div>
    <div v-else-if="!contact.is_subscribed_sms" class="text-muted text-center py-4">
      This contact is unsubscribed from SMS. Replies will still be received but you cannot send.
    </div>

    <div ref="messages" class="messages">
      <div v-if="initialLoading" class="text-center py-4"><b-spinner small /></div>
      <div v-else-if="!messages.length" class="text-muted text-center py-4">
        No messages yet. Send the first one below.
      </div>
      <div
        v-for="m in messages"
        :key="m.id"
        class="bubble-row"
        :class="m.direction === 'outbound' ? 'right' : 'left'"
      >
        <div class="bubble" :class="m.direction">
          <div class="bubble-body">{{ m.body }}</div>
          <div class="bubble-meta">
            {{ formatDateTime(m.created_at) }}
            <span v-if="m.direction === 'outbound'" class="ml-1">· {{ m.status }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="contact.phone && contact.is_subscribed_sms" class="composer">
      <b-form-textarea
        v-model="draft"
        rows="2"
        max-rows="4"
        placeholder="Type a message..."
        :disabled="sending"
        @keydown.enter.exact.prevent="send"
      />
      <div class="d-flex justify-content-between align-items-center mt-2">
        <small class="text-muted">{{ draft.length }} / 1600 · Enter to send</small>
        <b-button variant="primary" size="sm" :disabled="!draft.trim() || sending" @click="send">
          <b-spinner v-if="sending" small /> <span v-else>Send</span>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { formatDateTime } from '../../common/utils';

const POLL_MS = 10000;

export default {
  name: 'ContactSMSThread',
  props: {
    contact: { type: Object, required: true },
  },
  data() {
    return {
      messages: [],
      draft: '',
      initialLoading: true,
      sending: false,
      pollTimer: null,
    };
  },
  methods: {
    ...mapActions('sms', ['getContactSMSThread', 'sendSMS']),
    formatDateTime,
    async loadThread({ scroll = false } = {}) {
      try {
        const res = await this.getContactSMSThread(this.contact.id);
        const data = res.data !== undefined ? res.data : res;
        const next = Array.isArray(data) ? data : (data.results || []);
        const grew = next.length > this.messages.length;
        this.messages = next;
        if (scroll || grew) this.$nextTick(this.scrollToBottom);
      } catch (err) {
        // silent on poll failures
      } finally {
        this.initialLoading = false;
      }
    },
    scrollToBottom() {
      const el = this.$refs.messages;
      if (el) el.scrollTop = el.scrollHeight;
    },
    async send() {
      const body = this.draft.trim();
      if (!body || this.sending) return;
      this.sending = true;
      try {
        await this.sendSMS({ contact_id: this.contact.id, body });
        this.draft = '';
        // Twilio send is async via Celery — poll briefly until the row appears.
        await this.loadThread({ scroll: true });
        setTimeout(() => this.loadThread({ scroll: true }), 1500);
      } catch (err) {
        this.makeToast('danger', 'Failed to send SMS');
      } finally {
        this.sending = false;
      }
    },
    startPolling() {
      this.pollTimer = setInterval(() => this.loadThread(), POLL_MS);
    },
    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
  },
  mounted() {
    this.loadThread({ scroll: true });
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style scoped>
.sms-thread { display: flex; flex-direction: column; min-height: 400px; }
.messages {
  flex: 1;
  max-height: 480px;
  overflow-y: auto;
  padding: 8px 4px;
  background: #f8f9fb;
  border: 1px solid #e9edf2;
  border-radius: 6px;
}
.bubble-row { display: flex; margin: 6px 0; }
.bubble-row.right { justify-content: flex-end; }
.bubble-row.left { justify-content: flex-start; }
.bubble {
  max-width: 70%;
  padding: 8px 12px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.35;
  word-wrap: break-word;
  white-space: pre-wrap;
}
.bubble.outbound { background: #4e73df; color: #fff; border-bottom-right-radius: 4px; }
.bubble.inbound { background: #fff; color: #272C33; border: 1px solid #e9edf2; border-bottom-left-radius: 4px; }
.bubble-body { font-size: 14px; }
.bubble-meta { font-size: 11px; opacity: 0.75; margin-top: 4px; }
.bubble.outbound .bubble-meta { color: #e6ecff; }
.bubble.inbound .bubble-meta { color: #868e96; }
.composer { margin-top: 10px; }
</style>
